import type { ChannelAffiliateConfig, ChannelProduct } from "./types";

/**
 * Compose the final price using the simple MVP rule:
 *   coupon_price || sale_price || list_price
 */
export function computeFinalPrice(input: {
  listPrice?: number | null;
  salePrice?: number | null;
  couponPrice?: number | null;
}): number | null {
  if (input.couponPrice != null) return input.couponPrice;
  if (input.salePrice != null) return input.salePrice;
  if (input.listPrice != null) return input.listPrice;
  return null;
}

/**
 * Render a path template with variables.
 * Supported tokens: {{channelProductId}}, {{pid}}, {{clickId}}, {{subId}}, {{userId}}
 * Missing variables resolve to "".
 */
export function renderPathTemplate(
  template: string,
  vars: Record<string, string | number | null | undefined>,
): string {
  return template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key: string) => {
    const v = vars[key];
    if (v == null) return "";
    return encodeURIComponent(String(v));
  });
}

/**
 * Pick the right path for an outbound click. Preference order:
 *   1. The affiliate config's path_template (rendered)
 *   2. The channel_product.mini_program_path (rendered)
 *   3. The channel.default_path_template (rendered)
 */
export function resolveOutboundPath(opts: {
  channelProduct: Pick<ChannelProduct, "channelProductId" | "miniProgramPath">;
  channelDefaultPathTemplate: string | null;
  affiliateConfig: Pick<ChannelAffiliateConfig, "pathTemplate" | "pid"> | null;
  clickId: string;
  userId?: string | null;
}): string {
  const vars = {
    channelProductId: opts.channelProduct.channelProductId,
    clickId: opts.clickId,
    userId: opts.userId ?? "",
    pid: opts.affiliateConfig?.pid ?? "",
    subId: opts.clickId,
  };
  const tpl =
    opts.affiliateConfig?.pathTemplate ||
    opts.channelProduct.miniProgramPath ||
    opts.channelDefaultPathTemplate ||
    "";
  return renderPathTemplate(tpl, vars);
}

export function parseAliases(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v.map(String) : [];
  } catch {
    return [];
  }
}

export function stringifyAliases(aliases: string[] | null | undefined): string {
  if (!aliases || aliases.length === 0) return "[]";
  return JSON.stringify(aliases);
}

export function parseJsonObject(
  raw: string | null | undefined,
): Record<string, unknown> {
  if (!raw) return {};
  try {
    const v = JSON.parse(raw);
    return v && typeof v === "object" && !Array.isArray(v)
      ? (v as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function formatPriceCNY(price: number | null | undefined): string {
  if (price == null || Number.isNaN(price)) return "—";
  return `¥${price.toFixed(2)}`;
}

export function daysAgo(n: number): Date {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d;
}
